import { create } from 'zustand';
import { type ObtenerUsuarioUi } from '../schemas/usuarios/obtener-usuario';
import { type EditarUsuarioUi } from '../schemas/usuarios/editar-usuario';
import { UsuariosService } from '../services/usuarios.service';
import { useLoadingStore } from './loadingStore';
import { useAlertStore } from './alertStore';

interface FetchOptions {
  silent?: boolean;
}

interface UsuarioState {
  usuario: ObtenerUsuarioUi | null;
  setUsuario: (usuario: ObtenerUsuarioUi | null) => void;
  fetchUsuario: (options?: FetchOptions) => Promise<void>;
  editarUsuario: (datos: EditarUsuarioUi) => Promise<void>;
  cambiarContrasena: () => Promise<void>;
  clearUsuario: () => void;
}

export const useUsuarioStore = create<UsuarioState>((set) => ({
  usuario: null,
  setUsuario: (usuario) => set({ usuario }),
  clearUsuario: () => set({ usuario: null }),
  fetchUsuario: async (options: FetchOptions = {}) => {
    const { silent = false } = options;
    const setLoading = useLoadingStore.getState().setLoading;
    const showAlert = useAlertStore.getState().showAlert;

    if (!silent) {
      setLoading(true);
    }

    try {
      const data = await UsuariosService.obtenerUsuario();
      set({ usuario: data });
      
      if (!silent) {
        showAlert('Información del usuario cargada exitosamente', 'success');
      }
    } catch (error) {
      console.error('Error al obtener información del usuario:', error);
      const errorMessage = error instanceof Error ? error.message : 'Error desconocido al obtener información del usuario';
      showAlert(errorMessage, 'error');
    } finally {
      if (!silent) {
        setLoading(false);
      }
    }
  },
  editarUsuario: async (datos: EditarUsuarioUi) => {
    const setLoading = useLoadingStore.getState().setLoading;
    const showAlert = useAlertStore.getState().showAlert;

    setLoading(true);

    try {
      const currentUsuario = useUsuarioStore.getState().usuario;
      if (!currentUsuario) {
        throw new Error('No hay información de usuario disponible');
      }

      const datosParaEnviar = {
        ...datos
      };

      await UsuariosService.editarUsuario(datosParaEnviar);
      
      if (currentUsuario) {
        const usuarioActualizado = {
          ...currentUsuario,
          nombre: datos.nombre,
          apellido: datos.apellido,
          nombreCompleto: `${datos.nombre.charAt(0).toUpperCase() + datos.nombre.slice(1)} ${datos.apellido.charAt(0).toUpperCase() + datos.apellido.slice(1)}`
        };
        set({ usuario: usuarioActualizado });
      }
      
      showAlert('Información del usuario actualizada exitosamente', 'success');
    } catch (error) {
      console.error('Error al editar información del usuario:', error);
      const errorMessage = error instanceof Error ? error.message : 'Error desconocido al editar información del usuario';
      showAlert(errorMessage, 'error');
    } finally {
      setLoading(false);
    }
  },
  cambiarContrasena: async () => {
    const setLoading = useLoadingStore.getState().setLoading;
    const showAlert = useAlertStore.getState().showAlert;

    setLoading(true);

    try {
      await UsuariosService.cambiarContrasena();
      showAlert('Se ha enviado un correo electrónico con las instrucciones para cambiar tu contraseña', 'success');
    } catch (error) {
      console.error('Error al solicitar cambio de contraseña:', error);
      const errorMessage = error instanceof Error ? error.message : 'Error desconocido al solicitar cambio de contraseña';
      showAlert(errorMessage, 'error');
    } finally {
      setLoading(false);
    }
  }
})); 